import { useMemo } from 'react'
// import { PieChart } from 'src/components/analytics/charts/Pie'
import { NoData } from 'src/components/analytics/common/NoData'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { PieChart } from 'src/components/analytics/charts/AnimatedPie'
import { formatNumberToTwoDecimals, percentageWithFixedDecmials } from 'src/lib/utils/analytics'

type PaymentSplitData = {
  dimension: 'payment_split_count' | 'payment_split_percentage'
  data: Record<string, number>[]
}[]

const formatter = (value: number | string) => {
  if (typeof value === 'string') return `${value}%`
  return `${Math.abs(value)}%`
}

const PaymentSplitPieChart = () => {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.payment_split,
      dependencies: {
        endpoints: ['/payment-split']
      }
    },
    refreshKey
  })

  const paymentSplitData = useMemo(() => {
    const rawData: PaymentSplitData = data[0]?.data?.result?.widgetData?.data
    const comparedData: PaymentSplitData = data[0]?.data?.result?.widgetData?.compared_data || []

    if (!rawData || rawData.length === 0) return { data: [] }

    const paymentSplitPercentage = rawData.find(item => item.dimension === 'payment_split_percentage')?.data
    const comparedPaymentSplitPercentage = comparedData?.find(
      item => item.dimension === 'payment_split_percentage'
    )?.data

    const paymentSplitObj = Object.fromEntries(
      paymentSplitPercentage?.map((item, index) => {
        return [Object.values(item)[0], Object.values(item)[1]]
      }) || []
    )

    const comparedPaymentSplitObj = Object.fromEntries(
      comparedPaymentSplitPercentage?.map((item, index) => {
        return [Object.values(item)[0], Object.values(item)[1]]
      }) || []
    )
    const finalObject = Object.entries(paymentSplitObj).reduce((acc, item, index) => {
      const [key, value] = item
      acc.push({
        label: key,
        value: value.toString(),
        comparedValue: comparedPaymentSplitObj[key],
        change: comparedPaymentSplitObj[key] > 0 ? percentageWithFixedDecmials(value, comparedPaymentSplitObj[key]) : 0
      })
      return acc
    }, [] as { label: string; value: string; change: number; comparedValue: number }[])

    return {
      data: finalObject
    }
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (paymentSplitData.data.length > 0) {
      return <PieChart data={paymentSplitData.data} formatter={formatter} />
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}

export default PaymentSplitPieChart
